import React from 'react';
import Calendar from '..';
import { createRendererWithUniDriver } from '../../../../test/utils';
import { calendarUniDriverFactory } from '../Calendar.uni.driver';
import { LocaleUtils } from 'wix-style-react-tpa';
import { cleanup } from '@testing-library/react';
import { SupportedWixLocales } from 'wix-design-systems-locale-utils';

const isFirstLetterCapitalized = (str) =>
  str && str.length && str[0] === str[0].toLocaleUpperCase();

describe('Calendar', () => {
  describe('[async]', () => {
    runTests(createRendererWithUniDriver(calendarUniDriverFactory));
  });

  function runTests(render) {
    afterEach(cleanup);

    const SEPTEMBER = 8,
      OCTOBER = 9,
      NOVEMBER = 10;

    const monthNames =
      'January February March April May June July August September October November December'.split(
        ' '
      );

    describe('rendering the Calendar', () => {
      it('should display years from current date to 15 years into the future and past in the year drop-down', async () => {
        const { driver } = render(
          <Calendar showYearDropdown onChange={() => {}} />
        );

        const yearDriver = await driver.getYearDropdownDriver();
        const options = await yearDriver.options();
        const actualYears = await Promise.all(
          options.map(({ content }) => content())
        );

        const currentYear = new Date().getFullYear();
        const expectedYears = [];

        for (let year = currentYear + 15; year > currentYear - 30; year--) {
          expectedYears.push(year.toString());
        }
        // Adding loader content
        expectedYears.push('');

        expect(actualYears).toEqual(expectedYears);
      });

      it('should capitalize first letter of month in certain languages', async () => {
        const capitalizeMonthLanguages = SupportedWixLocales.filter((locale) =>
          LocaleUtils.CAPITALIZED_MONTH_LANGUAGES.includes(locale)
        );
        for (const locale of capitalizeMonthLanguages) {
          const { driver } = render(
            <Calendar
              value={new Date(2018, OCTOBER, 5)}
              onChange={() => {}}
              locale={locale}
            />
          );

          const monthCaption = await driver.getMonthCaption();
          expect(isFirstLetterCapitalized(monthCaption)).toBe(true);
        }
      });

      it('should display the month of the Date if the provided value is a single Date', async () => {
        const { driver } = render(
          <Calendar value={new Date(2018, NOVEMBER, 7)} onChange={() => {}} />
        );

        expect(await driver.getMonthCaption()).toEqual(monthNames[NOVEMBER]);
      });

      it('should display the current month if the provided value is undefined', async () => {
        const { driver } = render(<Calendar onChange={() => {}} />);

        expect(await driver.getMonthCaption()).toEqual(
          monthNames[new Date().getMonth()]
        );
      });

      it('should display the current month if the provided value is an empty object', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} />
        );

        expect(await driver.getMonthCaption()).toEqual(
          monthNames[new Date().getMonth()]
        );
      });

      it('should render 6 fixed weeks by default', async () => {
        const { driver } = render(
          <Calendar value={new Date('2025/2/1')} onChange={() => {}} />
        );

        expect(await driver.getWeeks().count()).toBe(6);
      });

      it('should not render fixed weeks', async () => {
        const { driver } = render(
          <Calendar
            value={new Date('2025/2/1')}
            onChange={() => {}}
            fixedWeeks={false}
          />
        );

        expect(await driver.getWeeks().count()).not.toBe(6);
      });
    });

    describe('Prevent Default', () => {
      it('should prevent default when clicking in header parts', async () => {
        const eventListenerMock = jest.fn((event) => event.persist());
        // We use a label wrapper, since a label's default is to delegate the click on to it's target. Just to demonstrate that this is a use-case that needs to be prevented.
        const { driver } = render(
          <label onClick={eventListenerMock}>
            <Calendar onChange={() => {}} showYearDropdown showMonthDropdown />
          </label>
        );

        await driver.clickOnPrevMonthButton();
        await driver.clickOnNextMonthButton();
        await driver.clickOnYearDropdown();
        await driver.clickOnMonthDropdown();

        expect(eventListenerMock).toHaveBeenCalledTimes(4);
        expect(eventListenerMock.mock.calls[0][0].defaultPrevented).toEqual(
          true
        );
        expect(eventListenerMock.mock.calls[1][0].defaultPrevented).toEqual(
          true
        );
        expect(eventListenerMock.mock.calls[2][0].defaultPrevented).toEqual(
          true
        );
        expect(eventListenerMock.mock.calls[3][0].defaultPrevented).toEqual(
          true
        );
      });
    });

    it('should call onMonthChange with the current month first day when changing the displayed month', async () => {
      const currentMonth = 2;
      const date = new Date(2021, currentMonth, 1);
      const expectedMonth = new Date(2021, currentMonth + 1, 1);
      const onMonthChange = jest.fn();
      const { driver } = render(
        <Calendar
          value={date}
          onMonthChange={onMonthChange}
          onChange={() => {}}
        />
      );
      expect(onMonthChange).toHaveBeenCalledTimes(0);

      await driver.clickOnNextMonthButton();

      expect(onMonthChange).toHaveBeenCalledTimes(1);
      const onMonthChangeValue = onMonthChange.mock.calls[0][0];
      expect(onMonthChangeValue.toLocaleDateString()).toEqual(
        expectedMonth.toLocaleDateString()
      );
    });

    it('should call onMonthChange with the current month first day when navigating with keyboard (next year)', async () => {
      const currentYear = 2020;
      const date = new Date(currentYear, 11, 31);
      const expectedMonth = new Date(currentYear + 1, 0, 1);
      const onMonthChange = jest.fn();
      const { driver } = render(
        <Calendar
          value={date}
          onMonthChange={onMonthChange}
          onChange={() => {}}
        />
      );
      expect(onMonthChange).toHaveBeenCalledTimes(0);

      await driver.keyboardNextYear();

      expect(onMonthChange).toHaveBeenCalledTimes(1);
      const onMonthChangeValue = onMonthChange.mock.calls[0][0];
      expect(onMonthChangeValue.toLocaleDateString()).toEqual(
        expectedMonth.toLocaleDateString()
      );
    });

    it('should call onMonthChange with the current month first day when navigating with keyboard (previous year)', async () => {
      const currentYear = 2021;
      const date = new Date(currentYear, 0, 1);
      const expectedMonth = new Date(currentYear - 1, 11, 1);
      const onMonthChange = jest.fn();
      const { driver } = render(
        <Calendar
          value={date}
          onMonthChange={onMonthChange}
          onChange={() => {}}
        />
      );
      expect(onMonthChange).toHaveBeenCalledTimes(0);

      await driver.keyboardPrevYear();

      expect(onMonthChange).toHaveBeenCalledTimes(1);
      const onMonthChangeValue = onMonthChange.mock.calls[0][0];
      expect(onMonthChangeValue.toLocaleDateString()).toEqual(
        expectedMonth.toLocaleDateString()
      );
    });

    describe('clicking on a day', () => {
      let onChange;

      beforeEach(() => {
        onChange = jest.fn();
      });

      it('should call onChange with the clicked day', async () => {
        const date = new Date(2018, 10, 5);
        const { driver } = await render(
          <Calendar value={date} onChange={onChange} />
        );

        expect(onChange).toHaveBeenCalledTimes(0);

        await driver.clickDay(new Date(2018, 10, 1));
        expect(onChange).toHaveBeenCalledTimes(1);
        expect(onChange.mock.calls[0][0].getDate()).toEqual(1);
      });

      it('should prevent event default', async () => {
        const date = new Date(2018, 10, 5);
        const { driver } = await render(
          <Calendar value={date} onChange={onChange} />
        );
        await driver.clickDay(new Date(2018, 10, 1));

        expect(onChange).toHaveBeenCalledTimes(1);
        expect(onChange.mock.calls[0][2].defaultPrevented).toEqual(true);
      });
    });

    describe('excludePastDates', () => {
      const defaultProps = {
        onChange: () => {},
      };

      it('past dates should not be active', async () => {
        const { driver } = render(
          <Calendar
            {...defaultProps}
            value={new Date(2020, 0, 17)}
            excludePastDates
          />
        );
        expect(await driver.isDayActive(new Date(2020, 0, 7))).toBe(false);
      });

      it('other days should be active', async () => {
        const { driver } = render(
          <Calendar {...defaultProps} value={new Date()} excludePastDates />
        );

        expect(await driver.isDayActive(new Date())).toBe(true);
      });

      it('when combined with filterDate prop disable more than one range of date', async () => {
        const today = new Date(2021, 2, 17);
        const yesterday = new Date(2021, 2, 16);
        const tomorrow = new Date(2021, 2, 18);
        const { driver } = render(
          <Calendar
            {...defaultProps}
            today={today}
            value={today}
            filterDate={(date) => date > new Date()}
            excludePastDates
          />
        );

        expect(await driver.isDayActive(yesterday)).toBe(false);
        expect(await driver.isDayActive(tomorrow)).toBe(false);
      });
    });

    describe('Auto Focus', () => {
      const defaultProps = {
        onChange: () => {},
      };
      it('should autoFocus on selected Day when autoFocus prop is true', async () => {
        const { driver } = render(
          <Calendar
            {...defaultProps}
            autoFocus={true}
            value={new Date(2018, 10, 10)}
          />
        );
        expect(await driver.getFocusedDay()).toBe('10');
      });

      it('should NOT autoFocus on selected Day when autoFocus prop is false', async () => {
        const { driver } = render(
          <Calendar
            {...defaultProps}
            value={new Date(2018, 10, 10)}
            autoFocus={false}
          />
        );
        expect(await driver.getFocusedDay()).toBe(null);
      });

      it('should NOT autoFocus on selected Day when rerendering without changing the autoFocus', async () => {
        const { driver, rerender } = render(
          <Calendar
            {...defaultProps}
            value={new Date(2018, 10, 10)}
            autoFocus
          />
        );
        expect(await driver.getFocusedDay()).toBe('10');

        rerender(
          <Calendar {...defaultProps} value={new Date(2018, 10, 9)} autoFocus />
        );

        expect(await driver.getFocusedDay()).toBe('10');
      });
    });

    describe(`'value' update`, () => {
      describe(`'month' state`, () => {
        async function testCase({
          initialValue,
          nextValue,
          expectedInitialMonth,
          expectedMonth,
          numOfMonths = 1,
        }: {
          initialValue?: Date;
          nextValue?: Date;
          expectedInitialMonth?: number;
          expectedMonth?: number;
          numOfMonths?: 1 | 2;
        }) {
          const props = {
            onChange: () => {},
            numOfMonths,
          };
          const { driver, rerender } = render(
            <Calendar {...props} value={initialValue} />
          );
          expect(await driver.getMonthCaption()).toEqual(
            monthNames[expectedInitialMonth]
          );
          rerender(<Calendar {...props} value={nextValue} />);
          expect(await driver.getMonthCaption()).toEqual(
            monthNames[expectedMonth]
          );
        }

        describe('one month', () => {
          describe('single day', () => {
            it('should not change the displayed month, provided that current month contains the new Date', async () => {
              await testCase({
                initialValue: new Date(2018, OCTOBER, 1),
                expectedInitialMonth: OCTOBER,
                nextValue: new Date(2018, OCTOBER, 2),
                expectedMonth: OCTOBER,
              });
            });

            it('should change the displayed month, provided that the current month is earlier than the new Date', async () => {
              await testCase({
                initialValue: new Date(2018, OCTOBER, 1),
                expectedInitialMonth: OCTOBER,
                nextValue: new Date(2018, NOVEMBER, 1),
                expectedMonth: NOVEMBER,
              });
            });

            it('should change the displayed month, provided that the current month is later than the new Date', async () => {
              await testCase({
                initialValue: new Date(2018, OCTOBER, 1),
                expectedInitialMonth: OCTOBER,
                nextValue: new Date(2018, SEPTEMBER, 1),
                expectedMonth: SEPTEMBER,
              });
            });
          });
        });

        describe('two month', () => {
          describe('single day', () => {
            it('should not change the displayed month, when new day is 2nd month', async () => {
              await testCase({
                initialValue: new Date(2018, OCTOBER, 1),
                expectedInitialMonth: OCTOBER,
                nextValue: new Date(2018, NOVEMBER, 2),
                expectedMonth: OCTOBER,
                numOfMonths: 2,
              });
            });
          });
        });
      });
    });

    describe('firstDayOfWeek', () => {
      it('should show correct first day of the week', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} firstDayOfWeek={0} />
        );

        expect(await driver.getNthWeekDayName(0)).toEqual('Sun');
      });

      it('should set sunday as first day of the week for vi locale', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} locale="vi" />
        );

        expect(await driver.getNthWeekDayName(0)).toEqual('CN');
      });
    });

    describe('formatWeekdayShort', () => {
      it('should set iiiiii as short weekday format by default', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} locale="lt" />
        );

        expect(await driver.getNthWeekDayName(0)).toEqual('pr');
      });

      it('should set iiiii as short weekday format for vi locale', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} locale="vi" />
        );

        expect(await driver.getNthWeekDayName(1)).toEqual('T2');
      });
    });

    describe('Month & Year dropdowns', () => {
      it('Should select a month & year', async () => {
        const { driver } = render(
          <Calendar
            value={new Date('01.01.01')}
            onChange={() => {}}
            showYearDropdown
            showMonthDropdown
          />
        );

        const monthDropdownDriver = await driver.getMonthDropdownDriver();

        expect(await driver.getMonthDropdownLabel()).toBe('January');
        await monthDropdownDriver.clickAtOptionWithValue('June');
        expect(await driver.getMonthDropdownLabel()).toBe('June');

        const yearDropdownDriver = await driver.getYearDropdownDriver();

        expect(await driver.getSelectedYear()).toBe('2001');
        await yearDropdownDriver.clickAtOptionWithValue('2020');
        expect(await driver.getSelectedYear()).toBe('2020');
      });
    });

    describe('locale', () => {
      it('should set the correct locale', async () => {
        const { driver } = render(
          <Calendar
            locale="es"
            value={new Date()}
            onChange={() => {}}
            firstDayOfWeek={0}
          />
        );

        expect(await driver.getNthWeekDayName(0)).toEqual('dom');
      });
    });

    describe('size', () => {
      it('should have default medium size', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} />
        );
        expect(await driver.getSize()).toEqual('medium');
      });

      it('should have size small [when] given', async () => {
        const { driver } = render(
          <Calendar value={new Date()} onChange={() => {}} size="small" />
        );
        expect(await driver.getSize()).toEqual('small');
      });
    });
  }
});
